import React, { useState } from 'react';
import { Platform, View, Text, Pressable, TextInput, StyleSheet } from 'react-native';
import type {
  AccountPracInfoResponseDto,
  AccountPlaDto,
  NotifyEventDto,
  SettingDto,
  AccountTouchpointsDto,
  AccountNotifyMessageDto,
  AccountTouchPointUpdateDto,
  TouchPointScheduleDto,
  TouchPointScheduleEventsDto,
} from '../../../types/api';
import { EDITOR_COLORS, editorText, webControlStyle, webCheckboxStyle } from '../../../styles/editorTheme';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import { resolveAuthContext } from '../../../services/auth/authContext';
import { ScheduleEventListEditor, SCHEDULE_LABEL_COLUMN_WIDTH } from './ScheduleEventListEditor';

interface TouchpointsViewProps {
  accountData?: AccountPracInfoResponseDto | null;
  isLoading?: boolean;
  error?: string | null;
  storage?: any;
  accountId?: string | null;
  apiService?: any;
}

const webDropdownStyle = { ...webControlStyle, height: 28 } as any;

// Reminder timing range: 30 days before the event down through 5 days after it.
const RDAYS_MIN = -30;
const RDAYS_MAX = 5;

const isTruthyValue = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'active' || normalized === 'enabled';
};

const parseRDays = (value: string): number => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatRDaysLabel = (value: number): string => {
  if (value < 0) {
    return `${Math.abs(value)} days before`;
  }
  if (value === 0) {
    return 'Day of event';
  }
  return `${value} days after`;
};

const reminderRowLabel = (index: number): string => (index === 0 ? 'Initial Notification' : `Reminder #${index}`);

// One-line summary per configured event, e.g. "Initial 16 days before" / "Reminder 5 days
// before" — used for the read-only schedule dropdown on the page table (not the edit form,
// which uses reminderRowLabel's numbered form instead).
const eventSummaryLabel = (index: number, event: NotifyEventDto): string =>
  `${index === 0 ? 'Initial' : 'Reminder'} ${formatRDaysLabel(parseRDays(event.RDays))}`;

// Row 0 (Initial Notification) can be anywhere in the full range. Every row after that must
// land at least 1 day later than the row above it — its floor is (row above's value + 1) —
// so the sequence always moves forward in time as you go down the list.
const optionsForRow = (index: number, draft: NotifyEventDto[]): number[] => {
  const floor = index === 0 ? RDAYS_MIN : Math.max(RDAYS_MIN, parseRDays(draft[index - 1]?.RDays ?? '') + 1);
  const options: number[] = [];
  for (let value = floor; value <= RDAYS_MAX; value += 1) {
    options.push(value);
  }
  return options;
};

const getSettingValue = (settings: SettingDto[], name: string): string =>
  settings.find((setting) => setting.Name === name)?.Value ?? '';

const setSettingValue = (settings: SettingDto[], name: string, value: string): SettingDto[] => {
  const index = settings.findIndex((setting) => setting.Name === name);
  if (index >= 0) {
    return settings.map((setting, i) => (i === index ? { ...setting, Value: value } : setting));
  }
  return [...settings, { Name: name, Value: value }];
};

const TERMINATE_DAYS_OPTIONS: string[] = Array.from({ length: 31 }, (_, i) => String(i));
const formatTerminateDaysLabel = (value: string): string => {
  const days = parseInt(value, 10) || 0;
  return `${days} day${days === 1 ? '' : 's'}`;
};

// Time of day in 30-minute increments, encoded as minutes-since-midnight strings ("0".."1410").
const DELIVER_TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => String(i * 30));
const formatDeliverTimeLabel = (value: string): string => {
  const minutesSinceMidnight = parseInt(value, 10) || 0;
  const hours24 = Math.floor(minutesSinceMidnight / 60) % 24;
  const minutes = minutesSinceMidnight % 60;
  const period = hours24 < 12 ? 'AM' : 'PM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

const DELIVER_ZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'ET', label: 'Eastern Time' },
  { value: 'CT', label: 'Central Time' },
  { value: 'MT', label: 'Mountain Time' },
  { value: 'PT', label: 'Pacific Time' },
  { value: 'AKT', label: 'Alaska Time' },
  { value: 'HT', label: 'Hawaii Time' },
];

const DO_NOT_NOTIFY_OPTION = { value: '', label: 'Do not notify' };

const NOTIFY_PREVIEW_LENGTH = 70;
const stripHtmlTags = (value: string): string => (value || '').replace(/<[^>]*>/g, '');
const notifyPreviewText = (value: string): string => {
  const stripped = stripHtmlTags(value);
  return stripped.length > NOTIFY_PREVIEW_LENGTH ? `${stripped.slice(0, NOTIFY_PREVIEW_LENGTH)}...` : stripped;
};

const scheduleEventSummaryLabel = (event: TouchPointScheduleEventsDto): string =>
  event.Name || `Offset ${event.Offset || '0'}`;

const NEW_SCHEDULE_KEY = '__new_schedule__';

const SCHEDULE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '1', label: 'Monitor' },
  { value: '2', label: 'Age-based' },
];

const renderCheckbox = (checked: boolean) => (
  <View style={[styles.checkboxBtn, checked && styles.checkboxBtnChecked]}>
    {checked ? <Text style={styles.checkboxMark}>✓</Text> : null}
  </View>
);

const renderWebCheckbox = (checked: boolean, onToggle: (nextChecked: boolean) => void, ariaLabel: string) =>
  React.createElement('input' as any, {
    type: 'checkbox',
    'aria-label': ariaLabel,
    checked,
    onChange: (event: any) => onToggle(Boolean(event?.target?.checked)),
    style: webCheckboxStyle,
  });

const webTextareaStyle = { ...webControlStyle, minHeight: 320, padding: 8, resize: 'vertical' } as any;
const webTextareaPlainStyle = { ...webControlStyle, minHeight: 160, padding: 8, resize: 'vertical' } as any;

const renderSettingDropdown = (
  value: string,
  onChange: (value: string) => void,
  options: Array<{ value: string; label: string }>,
  ariaLabel: string
) => {
  if (Platform.OS === 'web') {
    return React.createElement(
      'select' as any,
      {
        value,
        onChange: (e: any) => onChange(String(e?.target?.value ?? '')),
        style: webDropdownStyle,
        'aria-label': ariaLabel,
      },
      options.map((option) => React.createElement('option' as any, { key: option.value, value: option.value }, option.label))
    );
  }
  return <Text style={styles.reminderRowScheduleText}>{options.find((option) => option.value === value)?.label ?? ''}</Text>;
};

const resolveApiService = (apiService?: any): any => {
  return apiService
    || (globalThis as any).__proGroupsApiService
    || (globalThis as any).__childNavApiService;
};

const resolveUserStore = (): any => resolveAuthContext().userStore;

export const TouchpointsView: React.FC<TouchpointsViewProps> = ({
  accountData,
  isLoading,
  error,
  storage,
  accountId,
  apiService,
}) => {
  const resolvedApiService = resolveApiService(apiService);
  const plaList = accountData?.AccountSettings?.PlaList ?? [];
  const enabledPlas = plaList.filter((pla) => isTruthyValue(pla.Active));
  const enabledPlaOptions = enabledPlas.map((pla) => ({ value: pla.PlaId, label: pla.DName }));
  const touchpoints: AccountTouchpointsDto = accountData?.Touchpoints ?? { NotifyMsgs: [], Schedules: [] };
  const notifyMessages: AccountNotifyMessageDto[] = touchpoints.NotifyMsgs ?? [];
  const schedules: TouchPointScheduleDto[] = touchpoints.Schedules ?? [];
  const touchpointSchedulingEnabled = isTruthyValue(
    (accountData?.AccountSettings?.Enables ?? []).find((item) => item.Name.trim().toLowerCase() === 'enable_touchpointscheduling')?.Value
  );

  // SRAs eligible for the "Notify complete" / "Send report" recipient dropdowns.
  const notifySraOptions = (accountData?.SraList ?? []).filter((sra) => {
    const accessType = parseInt(sra.AccessType ?? '', 10);
    return !Number.isNaN(accessType) && accessType >= 10 && accessType < 20;
  });
  const sraNameOptions = [DO_NOT_NOTIFY_OPTION, ...notifySraOptions.map((sra) => ({ value: sra.SraId, label: sra.Name }))];
  const sraEmailOptions = (accountData?.SraList ?? [])
    .filter((sra) => !!sra.Email)
    .map((sra) => ({ value: sra.SraId, label: sra.Email }));

  const [editingPlaId, setEditingPlaId] = useState<string | null>(null);
  const [draftNotifySched, setDraftNotifySched] = useState<NotifyEventDto[]>([]);
  const [draftRemoteInfo, setDraftRemoteInfo] = useState<SettingDto[]>([]);
  const [editingNotifyType, setEditingNotifyType] = useState<string | null>(null);
  const [draftNotifyBody, setDraftNotifyBody] = useState('');
  const [draftNotifyPlain, setDraftNotifyPlain] = useState('');
  const [testEmailSraId, setTestEmailSraId] = useState('');
  const [editingScheduleKey, setEditingScheduleKey] = useState<string | null>(null);
  const [draftScheduleDName, setDraftScheduleDName] = useState('');
  const [draftScheduleType, setDraftScheduleType] = useState(SCHEDULE_TYPE_OPTIONS[0].value);
  const [draftScheduleAutoEnroll, setDraftScheduleAutoEnroll] = useState(false);
  const [draftScheduleStatus, setDraftScheduleStatus] = useState('1');
  const [draftScheduleEvents, setDraftScheduleEvents] = useState<TouchPointScheduleEventsDto[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveIsError, setSaveIsError] = useState(false);

  const editingPla = editingPlaId ? enabledPlas.find((pla) => pla.PlaId === editingPlaId) ?? null : null;
  const editingNotify = editingNotifyType ? notifyMessages.find((msg) => msg.NotifyType === editingNotifyType) ?? null : null;

  const closeNotifyEdit = () => {
    setEditingNotifyType(null);
    setDraftNotifyBody('');
    setDraftNotifyPlain('');
    setTestEmailSraId('');
    setSaveMessage(null);
    setSaveIsError(false);
  };

  const closeScheduleEdit = () => {
    setEditingScheduleKey(null);
    setDraftScheduleDName('');
    setDraftScheduleType(SCHEDULE_TYPE_OPTIONS[0].value);
    setDraftScheduleAutoEnroll(false);
    setDraftScheduleStatus('1');
    setDraftScheduleEvents([]);
    setSaveMessage(null);
    setSaveIsError(false);
  };

  const openEdit = (pla: AccountPlaDto) => {
    closeNotifyEdit();
    closeScheduleEdit();
    setEditingPlaId(pla.PlaId);
    setDraftNotifySched(
      pla.NotifySched.length > 0 ? pla.NotifySched.map((event) => ({ ...event })) : [{ RDays: '-14', NType: '' }]
    );
    setDraftRemoteInfo(pla.RemoteInfo.map((setting) => ({ ...setting })));
    setSaveMessage(null);
    setSaveIsError(false);
  };

  const closeEdit = () => {
    setEditingPlaId(null);
    setDraftNotifySched([]);
    setDraftRemoteInfo([]);
    setSaveMessage(null);
    setSaveIsError(false);
  };

  const openNotifyEdit = (msg: AccountNotifyMessageDto) => {
    closeEdit();
    closeScheduleEdit();
    setEditingNotifyType(msg.NotifyType);
    setDraftNotifyBody(msg.NotifyBody);
    setDraftNotifyPlain(msg.NotifyPlain);
    setTestEmailSraId('');
    setSaveMessage(null);
    setSaveIsError(false);
  };

  const openScheduleEdit = (schedule: TouchPointScheduleDto) => {
    closeEdit();
    closeNotifyEdit();
    setEditingScheduleKey(schedule.SchedId || NEW_SCHEDULE_KEY);
    setDraftScheduleDName(schedule.DName);
    setDraftScheduleType(schedule.Type || SCHEDULE_TYPE_OPTIONS[0].value);
    setDraftScheduleAutoEnroll(isTruthyValue(schedule.AutoEnroll));
    setDraftScheduleStatus(schedule.Status || '1');
    setDraftScheduleEvents(schedule.Events.map((event) => ({ ...event })));
    setSaveMessage(null);
    setSaveIsError(false);
  };

  const openAddSchedule = () => {
    closeEdit();
    closeNotifyEdit();
    setEditingScheduleKey(NEW_SCHEDULE_KEY);
    setDraftScheduleDName('');
    setDraftScheduleType(SCHEDULE_TYPE_OPTIONS[0].value);
    setDraftScheduleAutoEnroll(false);
    // '2' marks an unsaved, still-being-created schedule, distinct from the normal
    // Enabled('1')/Disabled('0') states — avoids it reading as already-enabled before saving.
    setDraftScheduleStatus('2');
    setDraftScheduleEvents([]);
    setSaveMessage(null);
    setSaveIsError(false);
  };

  const handleSaveScheduleEdit = async () => {
    if (!editingScheduleKey) {
      return;
    }
    const isAddingSchedule = editingScheduleKey === NEW_SCHEDULE_KEY;
    const originalSchedule = isAddingSchedule ? null : schedules.find((schedule) => schedule.SchedId === editingScheduleKey);

    const nextSchedule: TouchPointScheduleDto = {
      SchedId: '',
      LaunchInfo: [],
      RemoteInfo: [],
      OutcomeInst: '',
      ...originalSchedule,
      DName: draftScheduleDName,
      Type: draftScheduleType,
      AutoEnroll: draftScheduleAutoEnroll ? '1' : '0',
      Events: draftScheduleEvents,
      Status: draftScheduleStatus,
    };

    const nextSchedules = isAddingSchedule
      ? [...schedules, nextSchedule]
      : schedules.map((schedule) => (schedule.SchedId === editingScheduleKey ? nextSchedule : schedule));

    const nextTouchpoints: AccountTouchpointsDto = {
      ...touchpoints,
      Schedules: nextSchedules,
    };

    // The backend assigns SchedId on create — for a new schedule, trust the echoed-back
    // Touchpoints (which the backend guarantees mirrors what we sent, plus the new SchedId)
    // rather than what we sent, so the cache picks up the real id.
    const saved = await saveTouchpoints(
      plaList,
      nextTouchpoints,
      'Schedule saved.',
      isAddingSchedule
        ? (response, fallback) => {
            try {
              const parsed = typeof response?.payload === 'string' ? JSON.parse(response.payload) : response?.payload;
              if (parsed?.Touchpoints?.Schedules) {
                return parsed.Touchpoints as AccountTouchpointsDto;
              }
            } catch {
              // fall through to trusting what we sent
            }
            return fallback;
          }
        : undefined
    );
    if (saved) {
      closeScheduleEdit();
    }
  };

  const updateDraftRemoteInfo = (name: string, value: string) => {
    setDraftRemoteInfo((current) => setSettingValue(current, name, value));
  };

  const updateDraftRDays = (index: number, value: string) => {
    setDraftNotifySched((current) => {
      const next = current.map((event, i) => (i === index ? { ...event, RDays: value } : event));
      // Cascade forward: if this change pushed an earlier row's value past what a later row
      // currently holds, clamp that later row up to the new minimum it's allowed.
      for (let i = index + 1; i < next.length; i += 1) {
        const floor = Math.max(RDAYS_MIN, parseRDays(next[i - 1].RDays) + 1);
        if (parseRDays(next[i].RDays) < floor) {
          next[i] = { ...next[i], RDays: String(Math.min(floor, RDAYS_MAX)) };
        }
      }
      return next;
    });
  };

  const removeDraftEvent = (index: number) => {
    setDraftNotifySched((current) => current.filter((_, i) => i !== index));
  };

  const addDraftEvent = () => {
    setDraftNotifySched((current) => {
      const floor = current.length === 0 ? RDAYS_MIN : Math.max(RDAYS_MIN, parseRDays(current[current.length - 1].RDays) + 1);
      const nType = current[current.length - 1]?.NType ?? '';
      return [...current, { RDays: String(Math.min(floor, RDAYS_MAX)), NType: nType }];
    });
  };

  // Shared save path for both edit forms on this page — the backend's AccountTouchPointUpdateDto
  // always carries the full Plas + Touchpoints lists, so each caller passes through whichever
  // side it didn't touch unchanged.
  const saveTouchpoints = async (
    nextPlaList: AccountPlaDto[],
    nextTouchpoints: AccountTouchpointsDto,
    successMessage: string,
    resolveTouchpointsForStorage?: (response: any, fallback: AccountTouchpointsDto) => AccountTouchpointsDto
  ): Promise<boolean> => {
    const resolvedAccountId = (accountId || accountData?.AccountId || '').trim();

    if (!resolvedAccountId || !resolvedApiService?.processServiceRequestWithRefresh) {
      setSaveIsError(true);
      setSaveMessage('Could not save: account settings are not loaded yet.');
      return false;
    }

    const payload: AccountTouchPointUpdateDto = {
      AccountId: resolvedAccountId,
      Plas: nextPlaList,
      Touchpoints: nextTouchpoints,
      Status: '1',
    };

    try {
      setIsSaving(true);
      setSaveMessage(null);
      setSaveIsError(false);

      const response = await resolvedApiService.processServiceRequestWithRefresh({
        RequestType: 'provider_touchpoints_put',
        Payload: JSON.stringify(payload),
      }, 'sync', {
        userStore: resolveUserStore(),
        storage,
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Save failed.');
      }

      // Trust what we just sent rather than the backend's echoed-back DTO — same reasoning as
      // AccountSettingsView's own save — unless the caller opted into trusting the response
      // (e.g. a newly-created schedule whose SchedId is only known once the backend assigns it).
      const touchpointsForStorage = resolveTouchpointsForStorage
        ? resolveTouchpointsForStorage(response, nextTouchpoints)
        : nextTouchpoints;

      if (storage) {
        const pracStorage = new AccountPracInfoStorage(storage);
        const cached = await pracStorage.getAccountPracInfo(resolvedAccountId);
        if (cached?.AccountSettings) {
          const merged = {
            ...cached,
            AccountSettings: { ...cached.AccountSettings, PlaList: nextPlaList },
            Touchpoints: touchpointsForStorage,
          };
          await pracStorage.saveAccountPracInfo(resolvedAccountId, merged);
        }
      }

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('progroups:sra-config-updated', { detail: { accountId: resolvedAccountId } }));
      }

      setSaveMessage(successMessage);
      return true;
    } catch (err: any) {
      setSaveIsError(true);
      setSaveMessage(err?.message || 'Could not save.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!editingPla) {
      return;
    }
    const nextPlaList = plaList.map((pla) =>
      pla.PlaId === editingPla.PlaId ? { ...pla, NotifySched: draftNotifySched, RemoteInfo: draftRemoteInfo } : pla
    );
    const saved = await saveTouchpoints(nextPlaList, touchpoints, 'Notification schedule saved.');
    if (saved) {
      setEditingPlaId(null);
      setDraftNotifySched([]);
      setDraftRemoteInfo([]);
    }
  };

  const handleSaveNotifyMessage = async () => {
    if (!editingNotifyType) {
      return;
    }
    const nextTouchpoints: AccountTouchpointsDto = {
      ...touchpoints,
      NotifyMsgs: (touchpoints.NotifyMsgs ?? []).map((msg) =>
        msg.NotifyType === editingNotifyType ? { ...msg, NotifyBody: draftNotifyBody, NotifyPlain: draftNotifyPlain } : msg
      ),
    };
    const saved = await saveTouchpoints(plaList, nextTouchpoints, 'Notification message saved.');
    if (saved) {
      setEditingNotifyType(null);
      setDraftNotifyBody('');
      setDraftNotifyPlain('');
      setTestEmailSraId('');
    }
  };

  if (error) {
    return (
      <View style={styles.panel}>
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>Error</Text>
          <Text style={styles.errorBody}>{error}</Text>
        </View>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.panel}>
        <Text style={styles.loadingText}>Loading touchpoints...</Text>
      </View>
    );
  }

  return (
    <View style={styles.panel}>
      {saveMessage ? (
        <View style={styles.saveMessageRow}>
          <Text style={saveIsError ? styles.saveMessageError : styles.saveMessageText}>{saveMessage}</Text>
        </View>
      ) : null}

      {editingScheduleKey ? (
        <View style={styles.editFormPanel}>
          <View style={styles.editFormTopRow}>
            <Pressable style={styles.backInlineLink} onPress={closeScheduleEdit}>
              <Text style={styles.backInlineLinkText}>← Back</Text>
            </Pressable>
            <Pressable
              style={[styles.minimalSubmitBtn, isSaving && styles.minimalSubmitBtnDisabled]}
              onPress={() => { void handleSaveScheduleEdit(); }}
              disabled={isSaving}
            >
              <Text style={styles.minimalSubmitBtnText}>{isSaving ? 'Saving...' : 'Save'}</Text>
            </Pressable>
          </View>

          <View style={styles.scheduleFieldsTable}>
            <View style={styles.scheduleFieldsMainCol}>
              <View style={styles.scheduleFieldsRow}>
                <Text style={styles.scheduleFieldsLabel}>Schedule Name</Text>
                <View style={styles.scheduleNameInputWrap}>
                  <TextInput
                    style={styles.scheduleNameInput}
                    value={draftScheduleDName}
                    onChangeText={setDraftScheduleDName}
                    placeholder="Schedule name"
                    placeholderTextColor={EDITOR_COLORS.placeholder}
                  />
                </View>
                <View style={styles.scheduleAutoEnrollCell}>
                  {Platform.OS === 'web' ? (
                    renderWebCheckbox(draftScheduleAutoEnroll, setDraftScheduleAutoEnroll, 'Auto Enroll')
                  ) : (
                    <Pressable
                      style={styles.checkboxTouchable}
                      onPress={() => setDraftScheduleAutoEnroll((current) => !current)}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: draftScheduleAutoEnroll }}
                    >
                      {renderCheckbox(draftScheduleAutoEnroll)}
                    </Pressable>
                  )}
                  <Text style={styles.checkboxLabel}>Auto Enroll</Text>
                </View>
              </View>

              <View style={styles.scheduleFieldsRow}>
                <Text style={styles.scheduleFieldsLabel}>Type</Text>
                <View style={styles.scheduleTypeDropdownWrap}>
                  {renderSettingDropdown(draftScheduleType, setDraftScheduleType, SCHEDULE_TYPE_OPTIONS, 'Schedule type')}
                </View>
                <View style={styles.scheduleAutoEnrollCell} />
              </View>
            </View>

            <View style={styles.scheduleStatusMenuCol}>
              <Pressable
                style={styles.scheduleStatusLink}
                onPress={() => setDraftScheduleStatus((current) => (current === '1' ? '0' : '1'))}
              >
                <Text style={[styles.scheduleStatusLinkText, draftScheduleStatus === '1' && styles.scheduleStatusLinkTextDisable]}>
                  {draftScheduleStatus === '1' ? 'Disable' : 'Enable'}
                </Text>
              </Pressable>
            </View>
          </View>

          <View style={styles.scheduleSectionDivider} />

          <ScheduleEventListEditor
            key={editingScheduleKey}
            events={draftScheduleEvents}
            onChange={setDraftScheduleEvents}
            plaOptions={enabledPlaOptions}
          />
        </View>
      ) : null}

      {editingPla ? (
        <View style={styles.editFormPanel}>
          <View style={styles.editFormTopRow}>
            <Pressable style={styles.backInlineLink} onPress={closeEdit}>
              <Text style={styles.backInlineLinkText}>← Back</Text>
            </Pressable>
            <Pressable
              style={[styles.minimalSubmitBtn, isSaving && styles.minimalSubmitBtnDisabled]}
              onPress={() => { void handleSaveSchedule(); }}
              disabled={isSaving}
            >
              <Text style={styles.minimalSubmitBtnText}>{isSaving ? 'Saving...' : 'Save'}</Text>
            </Pressable>
          </View>

          <View style={styles.editFormTableWrap}>
            <View style={styles.editFormTable}>
              <View style={styles.editFormLabelCell}>
                <Text style={styles.editFormPlaName}>{editingPla.DName}</Text>
              </View>
              <View style={styles.editFormContentCell}>
                {draftNotifySched.map((event, index) => (
                  <View key={index} style={styles.reminderRow}>
                    <Text style={styles.reminderRowLabel}>{reminderRowLabel(index)}</Text>
                    <View style={styles.reminderRowDropdown}>
                      {Platform.OS === 'web' ? (
                        React.createElement(
                          'select' as any,
                          {
                            value: event.RDays,
                            onChange: (e: any) => updateDraftRDays(index, String(e?.target?.value ?? '')),
                            style: webDropdownStyle,
                            'aria-label': `${reminderRowLabel(index)} timing`,
                          },
                          optionsForRow(index, draftNotifySched).map((value) =>
                            React.createElement('option' as any, { key: value, value: String(value) }, formatRDaysLabel(value))
                          )
                        )
                      ) : (
                        <Text style={styles.reminderRowScheduleText}>{formatRDaysLabel(parseRDays(event.RDays))}</Text>
                      )}
                    </View>
                    <View style={styles.reminderRowAction}>
                      {index > 0 ? (
                        <Pressable style={styles.removeBtn} onPress={() => removeDraftEvent(index)}>
                          <Text style={styles.settingEditBtnText}>Remove</Text>
                        </Pressable>
                      ) : null}
                    </View>
                  </View>
                ))}

                <View style={styles.reminderRow}>
                  <View style={styles.reminderRowLabel} />
                  <View style={styles.reminderRowDropdown} />
                  <View style={styles.reminderRowAction}>
                    <Pressable style={styles.settingEditBtn2} onPress={addDraftEvent}>
                      <Text style={styles.settingEditBtnText}>Add Reminder</Text>
                    </Pressable>
                  </View>
                </View>

                <View style={styles.remoteInfoSpacer} />

                <View style={styles.reminderRow}>
                  <Text style={styles.reminderRowLabel}>Terminate after</Text>
                  <View style={styles.mergedDropdownCell}>
                    {renderSettingDropdown(
                      getSettingValue(draftRemoteInfo, 'terminatedays') || '10',
                      (value) => updateDraftRemoteInfo('terminatedays', value),
                      TERMINATE_DAYS_OPTIONS.map((value) => ({ value, label: formatTerminateDaysLabel(value) })),
                      'Terminate after'
                    )}
                  </View>
                </View>

                <View style={styles.reminderRow}>
                  <Text style={styles.reminderRowLabel}>Deliver</Text>
                  <View style={styles.mergedDropdownCellRow}>
                    <View style={styles.mergedDropdownHalf}>
                      {renderSettingDropdown(
                        getSettingValue(draftRemoteInfo, 'delivertime') || '480',
                        (value) => updateDraftRemoteInfo('delivertime', value),
                        DELIVER_TIME_OPTIONS.map((value) => ({ value, label: formatDeliverTimeLabel(value) })),
                        'Deliver time'
                      )}
                    </View>
                    <View style={styles.mergedDropdownHalf}>
                      {renderSettingDropdown(
                        getSettingValue(draftRemoteInfo, 'deliverzone') || DELIVER_ZONE_OPTIONS[0].value,
                        (value) => updateDraftRemoteInfo('deliverzone', value),
                        DELIVER_ZONE_OPTIONS,
                        'Deliver time zone'
                      )}
                    </View>
                  </View>
                </View>

                <View style={styles.remoteInfoSpacer} />

                <View style={styles.reminderRow}>
                  <Text style={styles.reminderRowLabel}>Notify complete</Text>
                  <View style={styles.mergedDropdownCell}>
                    {renderSettingDropdown(
                      getSettingValue(draftRemoteInfo, 'notifycompletesra'),
                      (value) => updateDraftRemoteInfo('notifycompletesra', value),
                      sraNameOptions,
                      'Notify complete'
                    )}
                  </View>
                </View>

                <View style={styles.reminderRow}>
                  <Text style={styles.reminderRowLabel}>Send report</Text>
                  <View style={styles.mergedDropdownCell}>
                    {renderSettingDropdown(
                      getSettingValue(draftRemoteInfo, 'printqueuesra'),
                      (value) => updateDraftRemoteInfo('printqueuesra', value),
                      sraNameOptions,
                      'Send report'
                    )}
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
      ) : null}

      {editingNotify ? (
        <View style={styles.editFormPanel}>
          <View style={styles.editFormTopRow}>
            <Pressable style={styles.backInlineLink} onPress={closeNotifyEdit}>
              <Text style={styles.backInlineLinkText}>← Back</Text>
            </Pressable>
            <Pressable
              style={[styles.minimalSubmitBtn, isSaving && styles.minimalSubmitBtnDisabled]}
              onPress={() => { void handleSaveNotifyMessage(); }}
              disabled={isSaving}
            >
              <Text style={styles.minimalSubmitBtnText}>{isSaving ? 'Saving...' : 'Save'}</Text>
            </Pressable>
          </View>

          <View style={styles.notifyEditHeaderRow}>
            <View style={styles.notifyEditSendTestGroup}>
              <Pressable
                style={[styles.settingEditBtn2, !testEmailSraId && styles.settingEditBtnDisabled]}
                disabled={!testEmailSraId}
                onPress={() => { /* TODO: wire to a send-test-email RequestType once one exists */ }}
              >
                <Text style={styles.settingEditBtnText}>Send Test</Text>
              </Pressable>
              <View style={styles.notifyEditTestEmailDropdown}>
                {renderSettingDropdown(
                  testEmailSraId,
                  setTestEmailSraId,
                  [{ value: '', label: 'Select recipient...' }, ...sraEmailOptions],
                  'Test email recipient'
                )}
              </View>
            </View>
            <Text style={styles.notifyEditDName}>{editingNotify.NotifyDName}</Text>
          </View>

          <View style={styles.notifyEditTextWrap}>
            <Text style={styles.notifyEditFieldLabel}>Message (HTML)</Text>
            {Platform.OS === 'web' ? (
              React.createElement('textarea', {
                value: draftNotifyBody,
                onChange: (e: any) => setDraftNotifyBody(String(e?.target?.value ?? '')),
                style: webTextareaStyle,
                'aria-label': `${editingNotify.NotifyDName} message HTML`,
              })
            ) : (
              <TextInput
                style={styles.notifyEditTextareaNative}
                value={draftNotifyBody}
                onChangeText={setDraftNotifyBody}
                multiline
                textAlignVertical="top"
              />
            )}
          </View>

          <View style={styles.notifyEditTextWrap}>
            <Text style={styles.notifyEditFieldLabel}>Message (Plain Text)</Text>
            {Platform.OS === 'web' ? (
              React.createElement('textarea', {
                value: draftNotifyPlain,
                onChange: (e: any) => setDraftNotifyPlain(String(e?.target?.value ?? '')),
                style: webTextareaPlainStyle,
                'aria-label': `${editingNotify.NotifyDName} message plain text`,
              })
            ) : (
              <TextInput
                style={styles.notifyEditTextareaPlainNative}
                value={draftNotifyPlain}
                onChangeText={setDraftNotifyPlain}
                multiline
                textAlignVertical="top"
              />
            )}
          </View>
        </View>
      ) : null}

      <View style={styles.pageTableSpacerRow} />
      <View style={styles.pageTableSpacerRow} />

      <View style={styles.editTable}>
        {touchpointSchedulingEnabled ? (
          <>
            <View style={styles.editTableRow}>
              <View style={styles.editTableLabelCell}>
                <Text style={styles.editTableLabel}>Touchpoint Schedules</Text>
              </View>
              <View style={styles.editTableContentCell}>
                {schedules.length === 0 ? (
                  <Text style={styles.placeholderText}>No touchpoint schedules configured.</Text>
                ) : (
                  <View style={styles.stackGroup}>
                    {schedules.map((schedule) => (
                      <View key={schedule.SchedId || schedule.DName} style={styles.plaRow}>
                        <Text style={styles.plaRowName}>{schedule.DName}</Text>
                        <View style={styles.plaRowSchedule}>
                          {Platform.OS === 'web' ? (
                            React.createElement(
                              'select' as any,
                              { defaultValue: '0', style: webDropdownStyle, 'aria-label': `${schedule.DName} events` },
                              schedule.Events.length === 0
                                ? [React.createElement('option' as any, { key: 'none', value: '0' }, 'No events configured')]
                                : schedule.Events.map((event, index) =>
                                  React.createElement('option' as any, { key: index, value: String(index) }, scheduleEventSummaryLabel(event))
                                )
                            )
                          ) : (
                            <Text style={styles.plaRowScheduleText}>
                              {schedule.Events.length === 0
                                ? 'No events configured'
                                : schedule.Events.map((event) => scheduleEventSummaryLabel(event)).join(', ')}
                            </Text>
                          )}
                        </View>
                        <Pressable
                          style={[styles.settingEditBtn, editingScheduleKey === (schedule.SchedId || NEW_SCHEDULE_KEY) && styles.settingEditBtnActive]}
                          onPress={() => openScheduleEdit(schedule)}
                        >
                          <Text style={styles.settingEditBtnText}>Edit</Text>
                        </Pressable>
                      </View>
                    ))}
                  </View>
                )}

                <View style={styles.addScheduleRow}>
                  <Pressable style={styles.settingEditBtn2} onPress={openAddSchedule}>
                    <Text style={styles.settingEditBtnText}>Add New</Text>
                  </Pressable>
                </View>
              </View>
            </View>

            <View style={styles.editTableSpacerRow}>
              <View style={styles.editTableSpacerLabelCell} />
              <View style={styles.editTableSpacerContentCell} />
            </View>
          </>
        ) : null}

        <View style={styles.editTableRow}>
          <View style={styles.editTableLabelCell}>
            <Text style={styles.editTableLabel}>Notification Setup</Text>
          </View>
          <View style={styles.editTableContentCell}>
            {enabledPlas.length === 0 ? (
              <Text style={styles.placeholderText}>No enabled assessments.</Text>
            ) : (
              <View style={styles.stackGroup}>
                {enabledPlas.map((pla) => (
                  <View key={pla.PlaId} style={styles.plaRow}>
                    <Text style={styles.plaRowName}>{pla.DName}</Text>
                    <View style={styles.plaRowSchedule}>
                      {Platform.OS === 'web' ? (
                        React.createElement(
                          'select' as any,
                          // Not disabled — the point is to let the SRA click it open to browse
                          // the full schedule at a glance. There's deliberately no onChange:
                          // picking a different option just changes which one shows once
                          // closed again, nothing is read from or saved anywhere.
                          { defaultValue: '0', style: webDropdownStyle, 'aria-label': `${pla.DName} notification schedule` },
                          pla.NotifySched.length === 0
                            ? [React.createElement('option' as any, { key: 'none', value: '0' }, 'No events configured')]
                            : pla.NotifySched.map((event, index) =>
                              React.createElement('option' as any, { key: index, value: String(index) }, eventSummaryLabel(index, event))
                            )
                        )
                      ) : (
                        <Text style={styles.plaRowScheduleText}>
                          {pla.NotifySched.length === 0
                            ? 'No events configured'
                            : pla.NotifySched.map((event, index) => eventSummaryLabel(index, event)).join(', ')}
                        </Text>
                      )}
                    </View>
                    <Pressable
                      style={[styles.settingEditBtn, editingPlaId === pla.PlaId && styles.settingEditBtnActive]}
                      onPress={() => openEdit(pla)}
                    >
                      <Text style={styles.settingEditBtnText}>Edit</Text>
                    </Pressable>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>

        <View style={styles.editTableSpacerRow}>
          <View style={styles.editTableSpacerLabelCell} />
          <View style={styles.editTableSpacerContentCell} />
        </View>

        <View style={styles.editTableRow}>
          <View style={styles.editTableLabelCell}>
            <Text style={styles.editTableLabel}>Customize Notifications</Text>
          </View>
          <View style={styles.editTableContentCell}>
            {notifyMessages.length === 0 ? (
              <Text style={styles.placeholderText}>No notification templates configured yet.</Text>
            ) : (
              <View style={styles.stackGroup}>
                {notifyMessages.map((msg) => (
                  <View key={msg.NotifyType} style={styles.notifyMsgCard}>
                    <View style={styles.notifyMsgHeaderRow}>
                      <Text style={styles.notifyMsgName}>{msg.NotifyDName}</Text>
                      <Pressable
                        style={[styles.settingEditBtn, editingNotifyType === msg.NotifyType && styles.settingEditBtnActive]}
                        onPress={() => openNotifyEdit(msg)}
                      >
                        <Text style={styles.settingEditBtnText}>Edit</Text>
                      </Pressable>
                    </View>
                    <View style={styles.notifyMsgPreviewBox}>
                      <Text style={styles.notifyMsgPreviewText}>{notifyPreviewText(msg.NotifyBody)}</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

const ACCOUNT_SPACING = {
  topPanelPadding: 14,
  sectionSpacerRow: 12,
  secondColumnContentTop: 2,
  secondColumnContentBottom: 6,
  secondColumnRowGap: 8,
};

const LABEL_COLUMN_WIDTH = 180;

const styles = StyleSheet.create({
  panel: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
    paddingTop: ACCOUNT_SPACING.topPanelPadding,
    backgroundColor: EDITOR_COLORS.background,
  },
  loadingText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  errorCard: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
  },
  errorTitle: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginBottom: 4,
  },
  errorBody: {
    ...editorText,
    lineHeight: 20,
    color: EDITOR_COLORS.errorText,
  },
  saveMessageRow: {
    paddingBottom: 10,
  },
  saveMessageText: {
    ...editorText,
    color: EDITOR_COLORS.successText,
  },
  saveMessageError: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
  },
  editFormPanel: {
    borderRadius: 6,
    backgroundColor: EDITOR_COLORS.background,
    paddingVertical: 10,
    marginBottom: 20,
  },
  editFormTopRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  backInlineLink: {
    paddingVertical: 4,
  },
  backInlineLinkText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
  },
  editFormTableWrap: {
    alignItems: 'center',
  },
  editFormTable: {
    flexDirection: 'row',
  },
  editFormLabelCell: {
    width: LABEL_COLUMN_WIDTH,
    borderRightWidth: 1,
    borderRightColor: EDITOR_COLORS.separator,
    paddingRight: 18,
    paddingTop: 10,
    alignItems: 'flex-end',
  },
  editFormPlaName: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    textAlign: 'right',
  },
  editFormContentCell: {
    paddingLeft: 18,
    paddingTop: 4,
    gap: 8,
  },
  reminderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  reminderRowLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    width: 140,
  },
  reminderRowDropdown: {
    width: 160,
  },
  reminderRowScheduleText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  reminderRowAction: {
    width: 100,
  },
  remoteInfoSpacer: {
    height: 12,
  },
  mergedDropdownCell: {
    width: 270,
  },
  mergedDropdownCellRow: {
    flexDirection: 'row',
    gap: 10,
  },
  mergedDropdownHalf: {
    width: 160,
  },
  notifyEditHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
    marginBottom: 10,
  },
  notifyEditSendTestGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  notifyEditTestEmailDropdown: {
    width: 200,
  },
  notifyEditDName: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    textAlign: 'right',
  },
  notifyEditTextWrap: {
    width: '100%',
    paddingHorizontal: 4,
    marginBottom: 12,
  },
  notifyEditFieldLabel: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    marginBottom: 4,
  },
  notifyEditTextareaNative: {
    ...editorText,
    minHeight: 320,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    padding: 8,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  notifyEditTextareaPlainNative: {
    ...editorText,
    minHeight: 160,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    padding: 8,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  scheduleFieldsTable: {
    flexDirection: 'row',
    alignItems: 'stretch',
    paddingHorizontal: 4,
    marginBottom: 4,
  },
  scheduleFieldsMainCol: {
    flex: 1,
    gap: 6,
  },
  scheduleFieldsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  scheduleSectionDivider: {
    height: 22,
    marginTop: 6,
  },
  scheduleFieldsLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    width: SCHEDULE_LABEL_COLUMN_WIDTH,
  },
  scheduleNameInputWrap: {
    width: 135,
  },
  scheduleNameInput: {
    ...editorText,
    height: 24,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  scheduleAutoEnrollCell: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: 160,
  },
  scheduleStatusMenuCol: {
    width: 140,
    borderLeftWidth: 1,
    borderLeftColor: EDITOR_COLORS.separator,
    paddingLeft: 16,
    paddingTop: 2,
  },
  scheduleStatusLink: {
    paddingVertical: 4,
    paddingLeft: 4,
    alignSelf: 'flex-start',
  },
  scheduleStatusLinkText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
  },
  scheduleStatusLinkTextDisable: {
    color: '#f87171',
  },
  scheduleTypeDropdownWrap: {},
  checkboxTouchable: {
    padding: 2,
  },
  checkboxBtn: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    backgroundColor: EDITOR_COLORS.background,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxBtnChecked: {
    backgroundColor: EDITOR_COLORS.accent,
    borderColor: EDITOR_COLORS.accent,
  },
  checkboxMark: {
    ...editorText,
    color: '#ffffff',
    fontSize: 12,
  },
  checkboxLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  notifyMsgCard: {
    gap: 6,
  },
  notifyMsgHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  notifyMsgName: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  notifyMsgPreviewBox: {
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  notifyMsgPreviewText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  pageTableSpacerRow: {
    height: ACCOUNT_SPACING.sectionSpacerRow,
  },
  editTable: {
    width: '100%',
  },
  editTableRow: {
    width: '100%',
    flexDirection: 'row',
    minHeight: 30,
  },
  editTableSpacerRow: {
    width: '100%',
    flexDirection: 'row',
    minHeight: ACCOUNT_SPACING.sectionSpacerRow,
  },
  editTableSpacerLabelCell: {
    width: LABEL_COLUMN_WIDTH,
  },
  editTableSpacerContentCell: {
    flex: 1,
  },
  editTableLabelCell: {
    width: LABEL_COLUMN_WIDTH,
    borderRightWidth: 1,
    borderRightColor: EDITOR_COLORS.separator,
    paddingLeft: 8,
    paddingRight: 18,
    paddingTop: 10,
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  editTableLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    textAlign: 'right',
  },
  editTableContentCell: {
    flex: 1,
    paddingLeft: 18,
    paddingRight: 12,
    paddingTop: ACCOUNT_SPACING.secondColumnContentTop,
    paddingBottom: ACCOUNT_SPACING.secondColumnContentBottom,
    justifyContent: 'flex-start',
  },
  placeholderText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    lineHeight: 20,
  },
  stackGroup: {
    gap: ACCOUNT_SPACING.secondColumnRowGap,
  },
  addScheduleRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingRight: 40,
    marginTop: ACCOUNT_SPACING.secondColumnRowGap,
  },
  plaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  plaRowName: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    width: 160,
  },
  plaRowSchedule: {
    width: 180,
  },
  plaRowScheduleText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  settingEditBtn: {
    width: 50,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingEditBtn2: {
    width: 120,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingEditBtnActive: {
    borderColor: '#93c5fd',
    backgroundColor: '#eff6ff',
  },
  settingEditBtnDisabled: {
    opacity: 0.5,
  },
  settingEditBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  removeBtn: {
    width: 70,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  minimalSubmitBtn: {
    width: 120,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  minimalSubmitBtnDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  minimalSubmitBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
});
